import { gql } from "@apollo/client";
import { Box, Chip, Typography, useTheme } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { WidgetHeader } from "components/atoms/WidgetHeader";
import { WidgetPaper } from "components/atoms/WidgetPaper";
import { CloudDataGrid } from "components/modules/CloudDataGrid";
import { MiniDisinfectionModes } from "components/modules/tableCells/MiniDisinfectionModes";
import {
  DisinfectionFilter,
  DisinfectionModes,
  DisinfectionRoomSort,
} from "gql/graphql";
import { useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useParams } from "react-router-dom";
import { useQueryWithSnack } from "utils/useQueryWithSnack";
import { getFormattedDate } from "utils/utilFunctions";
import { useDDFilter } from "../../modules/disinfectionFilter/DisinfectionFilterPanel";
import { GridSortModel, GridSortDirection } from "@mui/x-data-grid";

export const DISINFECTIONS_BY_ROOM_QUERY = gql`
  query DisinfectionsByRoom(
    $filter: DisinfectionFilter
    $sortBy: DisinfectionRoomSort
  ) {
    disinfectionRooms(filter: $filter, sortBy: $sortBy) {
      autonomous
      department
      lastDateDisinfected
      remoteControlled
      room
      stationary
      complete
      incomplete
      interruptions
    }
  }
`;

interface Props {
  hiddenFilter?: DisinfectionFilter;
  pageSize?: number;
  enableDDFilter?: boolean;
}

export const DisinfectionsByRoomTable = ({
  hiddenFilter,
  pageSize,
  enableDDFilter = false,
}: Props) => {
  const { orgId } = useParams();
  const [filterParams, setFilterParams] = useDDFilter();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const getSortByEnum = (
    field: string,
    sort: GridSortDirection,
  ): DisinfectionRoomSort => {
    const sortMap: Record<
      string,
      [DisinfectionRoomSort, DisinfectionRoomSort]
    > = {
      room: [DisinfectionRoomSort.Room, DisinfectionRoomSort.RoomDesc],
      department: [
        DisinfectionRoomSort.Department,
        DisinfectionRoomSort.DepartmentDesc,
      ],
      lastDateDisinfected: [
        DisinfectionRoomSort.LastDateDisinfected,
        DisinfectionRoomSort.LastDateDisinfectedDesc,
      ],
      remoteControlled: [
        DisinfectionRoomSort.RemoteControlled,
        DisinfectionRoomSort.RemoteControlledDesc,
      ],
      autonomous: [
        DisinfectionRoomSort.Autonomous,
        DisinfectionRoomSort.AutonomousDesc,
      ],
      stationary: [
        DisinfectionRoomSort.Stationary,
        DisinfectionRoomSort.StationaryDesc,
      ],
      complete: [
        DisinfectionRoomSort.Complete,
        DisinfectionRoomSort.CompleteDesc,
      ],
      incomplete: [
        DisinfectionRoomSort.Incomplete,
        DisinfectionRoomSort.IncompleteDesc,
      ],
      interruptions: [
        DisinfectionRoomSort.Interruptions,
        DisinfectionRoomSort.InterruptionsDesc,
      ],
      completionRate: [
        DisinfectionRoomSort.CompletionRate,
        DisinfectionRoomSort.CompletionRateDesc,
      ],
      total: [DisinfectionRoomSort.Total, DisinfectionRoomSort.TotalDesc],
      percentOfTotal: [
        DisinfectionRoomSort.PercentOfTotal,
        DisinfectionRoomSort.PercentOfTotalDesc,
      ],
    };

    const sortPair = sortMap[field];
    if (!sortPair || !sort) return DisinfectionRoomSort.LastDateDisinfectedDesc; // Default sort
    return sort === "asc" ? sortPair[0] : sortPair[1];
  };

  const { data, loading } = useQueryWithSnack(DISINFECTIONS_BY_ROOM_QUERY, {
    variables: {
      filter: {
        ...(enableDDFilter ? filterParams : {}),
        ...hiddenFilter,
        ...(orgId ? { organizationIds: [orgId] } : {}),
      },
      sortBy:
        sortModel.length > 0
          ? getSortByEnum(sortModel[0].field, sortModel[0].sort)
          : DisinfectionRoomSort.LastDateDisinfectedDesc,
    },
  });

  let sanitizedRooms;
  // Merge rooms "" and "Current location" into one room
  if (data?.disinfectionRooms) {
    sanitizedRooms = data.disinfectionRooms;
    const emptyRoom = data.disinfectionRooms.find((r: any) => r.room === "");
    const currentLocation = data.disinfectionRooms.find(
      (r: any) => r.room === "Current location",
    );

    sanitizedRooms = sanitizedRooms.filter(
      (r: any) => r.room !== "" && r.room !== "Current location",
    );

    if (emptyRoom && currentLocation) {
      sanitizedRooms.push({
        autonomous: emptyRoom.autonomous + currentLocation.autonomous,
        department: "", //LSBRI - TODO: This may not be correct
        lastDateDisinfected: new Date(
          Math.max(
            new Date(emptyRoom.lastDateDisinfected).getTime(),
            new Date(currentLocation.lastDateDisinfected).getTime(),
          ),
        ).toISOString(),
        remoteControlled:
          emptyRoom.remoteControlled + currentLocation.remoteControlled,
        room: "Current location",
        stationary: emptyRoom.stationary + currentLocation.stationary,
        complete: emptyRoom.complete + currentLocation.complete,
        incomplete: emptyRoom.incomplete + currentLocation.incomplete,
        interruptions: emptyRoom.interruptions + currentLocation.interruptions,
      });
    } else if (emptyRoom) {
      sanitizedRooms.push({
        ...emptyRoom,
        room: "Current location",
      });
    }
  }

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "room",
        flex: 1,
        maxWidth: 250,
        minWidth: 200,
        headerName: "Room name",
        renderCell: (params: GridRenderCellParams) => (
          <Typography fontWeight={500}>{params.value}</Typography>
        ),
      },
      {
        field: "department",
        headerName: "Department",
        flex: 1,
        minWidth: 200,
        maxWidth: 250,
        renderCell: (params: GridRenderCellParams) =>
          params.value !== "" ? (
            <Chip label={params.value} variant="outlined" size="small" />
          ) : null,
      },
      {
        field: "lastDateDisinfected",
        flex: 1,
        maxWidth: 150,
        align: "right",
        headerAlign: "right",
        headerName: "Last disinfected on",
        renderCell: (params) => getFormattedDate(new Date(params.value)),
      },
      {
        field: "interruptions",
        headerName: "Interruptions",
        maxWidth: 150,
        type: "number",
        flex: 1,
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          const theme = useTheme();
          return (
            <Typography sx={{ color: theme.palette.warning.main }}>
              {params.value}
            </Typography>
          );
        },
      },
      {
        field: "completionRate",
        headerName: "% Completion Rate",
        type: "number",
        maxWidth: 150,
        flex: 1,
        valueGetter: (params: GridRenderCellParams) => {
          const complete = Number(params.row.complete) || 0;
          const incomplete = Number(params.row.incomplete) || 0;
          const total = complete + incomplete;

          return Number(((complete / (total ? total : 1)) * 100).toFixed(1));
        },
        renderCell: (params: GridRenderCellParams) => (
          <Typography>{params.value}%</Typography>
        ),
      },
      {
        field: "incomplete",
        headerName: "Incomplete",
        align: "right",
        headerAlign: "right",
        flex: 1,
        maxWidth: 100,
        renderCell: (params: GridRenderCellParams) => {
          const theme = useTheme();
          return params.value > 0 ? (
            <Typography
              component="a"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setFilterParams({
                  ...filterParams,
                  rooms: [params.row.room],
                  departments: [params.row.department],
                  disinfectionStatus: "InComplete",
                });
              }}
              sx={{ color: theme.palette.error.main }}
            >
              {params.row.incomplete}
            </Typography>
          ) : (
            <Typography sx={{ color: theme.palette.error.main }}>
              {params.row.incomplete}
            </Typography>
          );
        },
      },
      {
        field: "complete",
        headerName: "Complete",
        align: "left",
        headerAlign: "left",
        flex: 1,
        maxWidth: 100,
        renderCell: (params: GridRenderCellParams) => {
          const theme = useTheme();
          return params.value > 0 ? (
            <Typography
              component="a"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setFilterParams({
                  ...filterParams,
                  rooms: [params.row.room],
                  departments: [params.row.department],
                  disinfectionStatus: "Complete",
                });
              }}
              sx={{ color: theme.palette.success.main }}
            >
              {params.row.complete}
            </Typography>
          ) : (
            <Typography sx={{ color: theme.palette.success.main }}>
              {params.row.complete}
            </Typography>
          );
        },
      },
      {
        field: "total",
        headerName: "Total",
        align: "right",
        headerAlign: "right",
        flex: 1,
        maxWidth: 100,
        valueGetter(params) {
          return (
            (params.row.autonomous ?? 0) +
            (params.row.remoteControlled ?? 0) +
            (params.row.stationary ?? 0)
          );
        },
        renderCell: (params: GridRenderCellParams) => {
          return params.value > 0 ? (
            <Typography
              component="a"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setFilterParams({
                  ...filterParams,
                  rooms: [params.row.room],
                  departments: [params.row.department],
                });
              }}
              sx={{ color: "black" }}
            >
              {params.value}
            </Typography>
          ) : (
            <Typography sx={{ color: "black" }}>{params.value}</Typography>
          );
        },
      },
      {
        field: "percentOfTotal",
        headerName: "% Of All",
        align: "left",
        headerAlign: "left",
        flex: 1,
        maxWidth: 200,
        valueGetter(params) {
          const grandTotal =
            params.api.getRowModels().size > 0
              ? Array.from(params.api.getRowModels().values()).reduce(
                  (sum: number, row: any) =>
                    sum +
                    ((row.autonomous ?? 0) +
                      (row.remoteControlled ?? 0) +
                      (row.stationary ?? 0)),
                  0,
                )
              : 0;

          if (grandTotal === 0) {
            return 0;
          }
          return Number(
            (
              (((params.row.autonomous ?? 0) +
                (params.row.remoteControlled ?? 0) +
                (params.row.stationary ?? 0)) /
                grandTotal) *
              100
            ).toFixed(1),
          );
        },
        renderCell: (params) => (
          <Typography>
            {params.value ? Number(params.value.toFixed(1)) : 0}%
          </Typography>
        ),
      },
      {
        field: "graph",
        headerName: "",
        renderCell: MiniDisinfectionModes,
        width: 76,
        sortable: false,
      },
      {
        field: "autonomous",
        headerName: "Predefined",
        align: "right",
        headerAlign: "right",
        minWidth: 80,
        renderCell: (params) => {
          const roomTotal =
            (params.row.autonomous ?? 0) +
            (params.row.remoteControlled ?? 0) +
            (params.row.stationary ?? 0);
          const percentage =
            roomTotal > 0
              ? Number((((params.value ?? 0) / roomTotal) * 100).toFixed(1))
              : 0;
          return (
            <>
              {params.row.autonomous > 0 ? (
                <Typography
                  component="a"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setFilterParams({
                      ...filterParams,
                      rooms: [params.row.room],
                      departments: [params.row.department],
                      disinfectionModes: [DisinfectionModes.Autonomous],
                    });
                  }}
                  sx={{ color: "black" }}
                >
                  {params.value ?? 0}
                </Typography>
              ) : (
                <Typography sx={{ color: "black" }}>
                  {params.value ?? 0}
                </Typography>
              )}
              <Typography
                sx={(theme) => ({
                  color: theme.palette.grey[600],
                  marginLeft: theme.spacing(1),
                })}
              >
                ({percentage}%)
              </Typography>
            </>
          );
        },
      },
      {
        field: "remoteControlled",
        headerName: "Remote Controlled",
        align: "right",
        headerAlign: "right",
        minWidth: 80,
        renderCell: (params) => {
          const roomTotal =
            (params.row.autonomous ?? 0) +
            (params.row.remoteControlled ?? 0) +
            (params.row.stationary ?? 0);
          const percentage =
            roomTotal > 0
              ? Number((((params.value ?? 0) / roomTotal) * 100).toFixed(1))
              : 0;
          return (
            <>
              {params.row.remoteControlled > 0 ? (
                <Typography
                  component="a"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setFilterParams({
                      ...filterParams,
                      rooms: [params.row.room],
                      departments: [params.row.department],
                      disinfectionModes: [DisinfectionModes.Stationary],
                    });
                  }}
                  sx={{ color: "black" }}
                >
                  {params.value ?? 0}
                </Typography>
              ) : (
                <Typography sx={{ color: "black" }}>
                  {params.value ?? 0}
                </Typography>
              )}
              <Typography
                sx={(theme) => ({
                  color: theme.palette.grey[600],
                  marginLeft: theme.spacing(1),
                })}
              >
                ({percentage}%)
              </Typography>
            </>
          );
        },
      },
      {
        field: "stationary",
        headerName: "Stationary",
        align: "right",
        headerAlign: "right",
        minWidth: 80,
        renderCell: (params) => {
          const roomTotal =
            (params.row.autonomous ?? 0) +
            (params.row.remoteControlled ?? 0) +
            (params.row.stationary ?? 0);
          const percentage =
            roomTotal > 0
              ? Number((((params.value ?? 0) / roomTotal) * 100).toFixed(1))
              : 0;
          return (
            <>
              {params.row.stationary > 0 ? (
                <Typography
                  component="a"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setFilterParams({
                      ...filterParams,
                      rooms: [params.row.room],
                      departments: [params.row.department],
                      disinfectionModes: [DisinfectionModes.RemoteControlled],
                    });
                  }}
                  sx={{ color: "black" }}
                >
                  {params.value ?? 0}
                </Typography>
              ) : (
                <Typography sx={{ color: "black" }}>
                  {params.value ?? 0}
                </Typography>
              )}
              <Typography
                sx={(theme) => ({
                  color: theme.palette.grey[600],
                  marginLeft: theme.spacing(1),
                })}
              >
                ({percentage}%)
              </Typography>
            </>
          );
        },
      },
    ],
    [filterParams, setFilterParams],
  );

  return (
    <WidgetPaper>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <WidgetHeader
          label={
            <FormattedMessage
              id="disinfection-by-room"
              defaultMessage="Disinfections per Room"
            />
          }
          description="Number of disinfection cycles per room"
        />
      </Box>
      <CloudDataGrid
        rows={sanitizedRooms || []}
        columns={columns || []}
        loading={loading}
        getRowId={(option) => option.lastDateDisinfected}
        pageSize={pageSize || 100}
        sortModel={sortModel}
        onSortModelChange={(model: GridSortModel) => setSortModel(model)}
        sortingMode="server"
      />
    </WidgetPaper>
  );
};
